import { m } from "@mojo/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { Calendar, Check, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/layout";
import { CycleCard, MilestoneTimeline } from "@/components/roadmap";
import { Badge, Card } from "@/components/ui";
import {
	type CycleScope,
	type CycleWithStats,
	getCurrentCycle,
	getCycleScope,
	getCycles,
	getUpcomingCycles,
} from "@/data/cycles";
import { getMilestones, type Milestone } from "@/data/issues";

export const Route = createFileRoute("/_authenticated/roadmap")({
	loader: async () => {
		const [allCycles, currentCycle, upcomingCycles, milestones] =
			await Promise.all([
				getCycles(),
				getCurrentCycle(),
				getUpcomingCycles(),
				getMilestones(),
			]);

		// Separate completed cycles from all cycles
		const completedCycles = allCycles.filter((c) => c.status === "completed");

		// Get scope for current cycle if exists
		let currentCycleScope: CycleScope[] = [];
		if (currentCycle) {
			currentCycleScope = await getCycleScope({ data: currentCycle.id });
		}

		return {
			currentCycle,
			currentCycleScope,
			upcomingCycles,
			completedCycles,
			milestones,
		};
	},
	component: RoadmapPage,
});

// Type colors for scope items
const typeColors = {
	bug: "bg-bug",
	improvement: "bg-improvement",
	feature: "bg-feature",
};

function RoadmapPage() {
	const {
		currentCycle,
		currentCycleScope,
		upcomingCycles,
		completedCycles,
		milestones,
	} = Route.useLoaderData();

	return (
		<div>
			<PageHeader
				title={m.roadmap_title()}
				description={m.roadmap_description()}
			/>

			{/* Current Cycle Highlight */}
			{currentCycle && (
				<div className="mb-8">
					<div className="mb-4 flex items-center gap-2">
						<div className="h-2 w-2 animate-pulse rounded-full bg-status-progress" />
						<h2 className="font-mono text-sm text-text-secondary uppercase tracking-wider">
							{m.roadmap_current_cycle()}
						</h2>
					</div>

					<CurrentCycleCard cycle={currentCycle} scope={currentCycleScope} />
				</div>
			)}

			{/* Upcoming Cycles */}
			{upcomingCycles.length > 0 && (
				<div className="mb-8">
					<div className="mb-4 flex items-center gap-2">
						<ChevronRight className="h-4 w-4 text-accent" />
						<h2 className="font-mono text-sm text-text-secondary uppercase tracking-wider">
							{m.roadmap_upcoming()}
						</h2>
						<Badge variant="muted" size="sm">
							{upcomingCycles.length}
						</Badge>
					</div>

					<div className="space-y-3">
						{upcomingCycles.map((cycle) => (
							<CycleCard key={cycle.id} cycle={cycle} />
						))}
					</div>
				</div>
			)}

			{/* Completed Cycles */}
			{completedCycles.length > 0 && (
				<div className="mb-8">
					<div className="mb-4 flex items-center gap-2">
						<Check className="h-4 w-4 text-status-done" />
						<h2 className="font-mono text-sm text-text-secondary uppercase tracking-wider">
							{m.roadmap_completed()}
						</h2>
						<Badge variant="success" size="sm">
							{completedCycles.length}
						</Badge>
					</div>

					<div className="space-y-3">
						{completedCycles.map((cycle) => (
							<div
								key={cycle.id}
								className="opacity-70 transition-opacity hover:opacity-100"
							>
								<CycleCard cycle={cycle} />
							</div>
						))}
					</div>
				</div>
			)}

			{/* Empty state */}
			{!currentCycle &&
				upcomingCycles.length === 0 &&
				completedCycles.length === 0 && (
					<Card>
						<div className="py-12 text-center">
							<Calendar className="mx-auto mb-4 h-12 w-12 text-text-tertiary" />
							<p className="mb-2 text-text-secondary">
								{m.roadmap_no_cycles()}
							</p>
							<p className="text-sm text-text-tertiary">
								{m.roadmap_no_cycles_description()}
							</p>
						</div>
					</Card>
				)}

			{/* Timeline Section */}
			{milestones.length > 0 && (
				<div className="mt-12 border-border border-t pt-8">
					<MilestoneTimeline milestones={milestones as Milestone[]} />
				</div>
			)}
		</div>
	);
}

// Current Cycle Card - special highlighted version
function CurrentCycleCard({
	cycle,
	scope,
}: {
	cycle: CycleWithStats;
	scope: CycleScope[];
}) {
	const progress =
		cycle.totalIssues > 0
			? Math.round((cycle.completedIssues / cycle.totalIssues) * 100)
			: cycle.progress;

	return (
		<div className="relative overflow-hidden rounded-xl border-2 border-status-progress/30 bg-bg-elevated p-6">
			{/* Glow Effect */}
			<div className="-translate-y-1/2 absolute top-0 right-0 h-64 w-64 translate-x-1/2 rounded-full bg-status-progress/5 blur-3xl" />

			<div className="relative">
				{/* Header */}
				<div className="mb-4 flex items-start justify-between gap-4">
					<div>
						<h3 className="font-semibold text-text-primary text-xl">
							{cycle.name || `Cycle ${cycle.number}`}
						</h3>
						{cycle.description && (
							<p className="mt-1 text-sm text-text-secondary">
								{cycle.description}
							</p>
						)}
					</div>
					<Badge variant="warning" dot pulse>
						In Progress
					</Badge>
				</div>

				{/* Progress Bar */}
				<div className="mb-4">
					<div className="mb-2 flex justify-between font-mono text-text-tertiary text-xs">
						<span>
							{m.roadmap_issues_of({
								completed: String(cycle.completedIssues),
								total: String(cycle.totalIssues),
							})}
						</span>
						<span className="text-accent">{progress}%</span>
					</div>
					<div className="h-3 overflow-hidden rounded-full bg-bg-secondary">
						<div
							className="h-full rounded-full bg-gradient-to-r from-status-progress to-accent transition-all duration-1000"
							style={{ width: `${progress}%` }}
						/>
					</div>
				</div>

				{/* Scope Preview */}
				{scope.length > 0 && (
					<div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
						{scope.slice(0, 6).map((item) => (
							<div
								key={item.id}
								className="flex items-center gap-2 rounded-lg border border-border bg-bg-secondary/50 px-3 py-2"
							>
								<div
									className={`h-2 w-2 rounded-full ${typeColors[item.type]}`}
								/>
								<span className="flex-1 truncate text-sm text-text-primary">
									{item.title}
								</span>
								{item.status === "completed" && (
									<Check className="ml-auto h-4 w-4 flex-shrink-0 text-status-done" />
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
